import { useMemo } from 'react';

import type { VehicleMatch } from '@ltvco/refresh-lib/api';
import type { Message } from '@ltvco/refresh-lib/v2';

export interface MessagePairType {
  id: string;
  user: Message;
  assistant: Message | null;
  userIndex: number;
  assistantIndex: number | null;
}

interface UseMessagePairingReturn {
  pairs: MessagePairType[];
  firstMatchmakerAssistantIndex: number;
  firstMatchmakerData: VehicleMatch[] | null;
  hasError: boolean;
}

/**
 * Hook to pair user and assistant messages and extract matchmaker data
 */
export const useMessagePairing = (
  messages: Message[],
): UseMessagePairingReturn => {
  return useMemo(() => {
    const firstMatchmakerAssistantIndex = messages.findIndex((msg) => {
      const data = (msg as unknown as { vehicleMatchmakerData?: unknown })
        .vehicleMatchmakerData;
      return (
        msg.role === 'assistant' &&
        data != null &&
        typeof data === 'object' &&
        'matches' in data
      );
    });

    const firstMatchmakerData =
      firstMatchmakerAssistantIndex !== -1
        ? ((
            messages[firstMatchmakerAssistantIndex] as unknown as {
              vehicleMatchmakerData?: { matches?: VehicleMatch[] };
            }
          ).vehicleMatchmakerData?.matches ?? null)
        : null;

    const hasError = messages.some((msg) => {
      if (msg.role !== 'assistant') return false;
      const data = (msg as unknown as { data?: unknown }).data;
      return Boolean(data && typeof data === 'object' && 'error' in data);
    });

    const pairs: MessagePairType[] = [];
    for (let i = 0; i < messages.length; i++) {
      const userMsg = messages[i];
      if (userMsg.role !== 'user') continue;

      const maybeAssistant = messages[i + 1];
      const assistantMsg =
        maybeAssistant && maybeAssistant.role === 'assistant'
          ? maybeAssistant
          : null;
      const assistantIndex = assistantMsg ? i + 1 : null;

      // Skip the pair whose assistant message is rendered as the results card.
      if (
        assistantIndex !== null &&
        assistantIndex === firstMatchmakerAssistantIndex
      ) {
        continue;
      }

      pairs.push({
        id: `pair-${i}`,
        user: userMsg,
        assistant: assistantMsg,
        userIndex: i,
        assistantIndex,
      });
    }

    return {
      pairs,
      firstMatchmakerAssistantIndex,
      firstMatchmakerData,
      hasError,
    };
  }, [messages]);
};
